import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { applySlotZooms, buildArtboardCss, MC_FALLBACK_LOGO_PATH } from './mediaCreatorRenderDocument';
import { applyElementLayout } from './mediaCreatorElementLayout';
import {
  elementLayoutKeyFromElement,
  findLogoImage,
  findSelectableElement,
  isLogoImage,
  normalizeArtboardDom,
  queryLogoImages,
} from './mediaCreatorDomRegistry';
import MediaCreatorHtmlSelectionOverlay, {
  isTypingInHost,
} from './MediaCreatorHtmlSelectionOverlay';
import {
  applyCutoutState,
  attachPhotoLoadFallback,
  CUTOUT_ATTR,
  lookupAssetUrl,
  renditionForAsset,
  resolveLogoUrl,
  resolvePhotoUrl,
} from './mediaCreatorHtmlResolve';
import {
  appliedPhotoZoom,
  applyPhotoZoom,
  clampPhotoZoom,
  CUTOUT_OBJECT_FIT,
  formatObjectPosition,
  objectPositionForPhoto,
  parseObjectPosition,
  PHOTO_OBJECT_FIT,
} from './mediaCreatorPhotoFit';

const NO_ZOOM = {};
/** Exponential so each wheel notch zooms by the same ratio. */
const WHEEL_ZOOM_RATE = 0.0015;
const WHEEL_COMMIT_MS = 150;

function resolvePhotoSlotFromClick(target, host) {
  if (!target || !host) return null;
  if (isLogoImage(target.closest?.('img') || target)) return null;
  if (target.closest?.('.mc-logo-wrap')) return null;
  if (target.closest?.('.mc-copy') || target.closest?.('[data-editable]')) return null;
  if (target.closest?.('.mc-slot-caption')) return null;
  if (target.matches?.('img[data-photo-slot]')) {
    return target.getAttribute('data-photo-slot');
  }
  const figure = target.closest?.('.mc-gallery figure.mc-photo');
  if (figure && figure.contains(target) && !target.closest?.('.mc-slot-caption')) {
    const img = figure.querySelector('img[data-photo-slot]');
    if (target === figure || target === img) {
      return img?.getAttribute('data-photo-slot') || null;
    }
  }
  return null;
}

const MediaCreatorHtmlArtboard = forwardRef(
  (
    {
      layout,
      assetUrls,
      scale = 1,
      selectedPhotoSlot,
      onSelectPhotoSlot,
      onDocumentHtmlChange,
      onPhotoObjectPositionChange,
      onPhotoZoomChange,
      selectedElementKey,
      onSelectElementKey,
      onElementLayoutChange,
      onDeleteElement,
      photoSlotTool = 'crop',
      showSlotCaptions = false,
      presentation = false,
    },
    ref,
  ) => {
    const w = layout?.size_px?.width || 1920;
    const h = layout?.size_px?.height || 1080;
    const hostRef = useRef(null);
    const appliedHtmlRef = useRef('');
    const panRef = useRef(null);
    const bindings = layout?.photo_bindings || {};
    const slotPositions = layout?.photo_object_positions || {};
    const elementLayout = layout?.element_layout || {};
    const photoZoom = layout?.photo_zoom || NO_ZOOM;
    const logoId = layout?.logo_binding;
    const documentHtml = layout?.document_html || '';
    const artboardCss = useMemo(
      () => buildArtboardCss(layout),
      [layout?.document_css, layout?.size_px?.width, layout?.size_px?.height],
    );

    const hydrateImages = useCallback(() => {
      const root = hostRef.current;
      if (!root) return;
      queryLogoImages(root).forEach((img) => {
        const resolved = resolveLogoUrl(logoId, assetUrls);
        // A bound logo whose URL is still loading keeps its current image instead of flashing the fallback.
        const url = resolved || (logoId && img.getAttribute('src') ? null : MC_FALLBACK_LOGO_PATH);
        if (url) {
          img.removeAttribute('crossorigin');
          img.src = url;
          if (logoId) {
            img.setAttribute('data-media-asset-id', String(logoId));
            img.setAttribute(
              'data-rendition',
              renditionForAsset(lookupAssetUrl(assetUrls, logoId), { logo: true }),
            );
          }
        }
      });
      root.querySelectorAll('img[data-photo-slot]').forEach((img) => {
        const slot = img.getAttribute('data-photo-slot');
        const assetId = slot ? bindings[slot] : null;
        const url = resolvePhotoUrl(assetId, assetUrls);
        if (url && assetId) {
          const info = lookupAssetUrl(assetUrls, assetId);
          img.removeAttribute('crossorigin');
          img.src = url;
          img.setAttribute('data-media-asset-id', String(assetId));
          img.setAttribute('data-rendition', renditionForAsset(info));
          const cutout = applyCutoutState(img, info);
          img.style.objectFit = cutout ? CUTOUT_OBJECT_FIT : PHOTO_OBJECT_FIT;
          img.style.objectPosition = cutout
            ? slotPositions[slot] || '50% 50%'
            : objectPositionForPhoto(assetUrls, assetId, [slotPositions[slot], '50% 50%']);
          attachPhotoLoadFallback(img, assetId, assetUrls);
        } else {
          applyCutoutState(img, null);
          img.removeAttribute('src');
          img.removeAttribute('data-media-asset-id');
        }
      });
      applyElementLayout(root, elementLayout);
      applySlotZooms(root, { photo_bindings: bindings, photo_zoom: photoZoom });
    }, [assetUrls, bindings, elementLayout, logoId, slotPositions, photoZoom]);

    const latestRef = useRef({});
    latestRef.current = { bindings, photoZoom, onPhotoZoomChange };
    const wheelRef = useRef({ slot: null, zoom: 1, timer: null });

    useEffect(() => {
      const root = hostRef.current;
      if (!root || presentation) return undefined;
      const onWheel = (e) => {
        if (!(e.ctrlKey || e.metaKey)) return;
        const target = e.target;
        const img = target.closest?.('img[data-photo-slot]')
          || target.closest?.('figure, .mc-photo')?.querySelector('img[data-photo-slot]');
        if (!img || !root.contains(img)) return;
        const slot = img.getAttribute('data-photo-slot');
        const { bindings: b, photoZoom: zooms, onPhotoZoomChange: commit } = latestRef.current;
        if (!slot || b[slot] == null || !commit) return;
        e.preventDefault();
        const pending = wheelRef.current;
        const current = pending.slot === slot && pending.timer ? pending.zoom : (zooms[slot] ?? appliedPhotoZoom(img));
        const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
        const next = clampPhotoZoom(current * Math.exp(-delta * WHEEL_ZOOM_RATE), {
          cutout: img.hasAttribute(CUTOUT_ATTR),
        });
        applyPhotoZoom(img, next, img.style.objectPosition);
        if (pending.timer && pending.slot !== slot) {
          window.clearTimeout(pending.timer);
          commit(pending.slot, pending.zoom);
        }
        if (pending.timer) window.clearTimeout(pending.timer);
        wheelRef.current = {
          slot,
          zoom: next,
          timer: window.setTimeout(() => {
            wheelRef.current = { slot: null, zoom: 1, timer: null };
            commit(slot, next);
          }, WHEEL_COMMIT_MS),
        };
      };
      root.addEventListener('wheel', onWheel, { passive: false });
      return () => {
        root.removeEventListener('wheel', onWheel);
        const pending = wheelRef.current;
        if (pending.timer) {
          window.clearTimeout(pending.timer);
          latestRef.current.onPhotoZoomChange?.(pending.slot, pending.zoom);
          wheelRef.current = { slot: null, zoom: 1, timer: null };
        }
      };
    }, [presentation]);

    const attachEditable = useCallback(() => {
      if (presentation) return undefined;
      const root = hostRef.current;
      if (!root) return undefined;
      const fields = root.querySelectorAll('[data-editable]');
      const lastContent = new WeakMap();
      fields.forEach((el) => lastContent.set(el, el.innerHTML));
      const sync = () => {
        const main = root.querySelector('.mc-invite');
        if (!main || !onDocumentHtmlChange) return;
        let edited = false;
        fields.forEach((el) => {
          if (el.innerHTML !== lastContent.get(el)) {
            edited = true;
            lastContent.set(el, el.innerHTML);
          }
        });
        onDocumentHtmlChange(main.outerHTML, { auto: !edited });
      };
      const timers = new WeakMap();
      const inputHandlers = new WeakMap();
      fields.forEach((el) => {
        el.setAttribute('contenteditable', 'true');
        el.setAttribute('spellcheck', 'true');
        el.addEventListener('blur', sync);
        const onInput = () => {
          const prev = timers.get(el);
          if (prev) window.clearTimeout(prev);
          timers.set(
            el,
            window.setTimeout(() => {
              sync();
            }, 250),
          );
        };
        inputHandlers.set(el, onInput);
        el.addEventListener('input', onInput);
      });
      return () => {
        fields.forEach((el) => {
          el.removeEventListener('blur', sync);
          const onInput = inputHandlers.get(el);
          if (onInput) el.removeEventListener('input', onInput);
          const prev = timers.get(el);
          if (prev) window.clearTimeout(prev);
        });
      };
    }, [onDocumentHtmlChange, presentation]);

    useEffect(() => {
      const root = hostRef.current;
      if (!root || documentHtml === appliedHtmlRef.current) return undefined;
      root.innerHTML = documentHtml;
      normalizeArtboardDom(root);
      const main = root.querySelector('.mc-invite');
      const logoEl = findLogoImage(root);
      if (
        !presentation
        && main
        && onDocumentHtmlChange
        && logoEl
        && !logoEl.hasAttribute('data-logo-slot')
      ) {
        onDocumentHtmlChange(main.outerHTML, { auto: true });
        appliedHtmlRef.current = main.outerHTML;
      } else {
        appliedHtmlRef.current = documentHtml;
      }
      hydrateImages();
      return attachEditable();
    }, [documentHtml, hydrateImages, attachEditable, presentation, onDocumentHtmlChange]);

    useEffect(() => {
      hydrateImages();
    }, [hydrateImages, bindings, logoId, assetUrls, elementLayout]);

    const onHostClick = (e) => {
      const host = hostRef.current;
      const slot = resolvePhotoSlotFromClick(e.target, host);
      if (slot) {
        onSelectPhotoSlot?.(slot);
        return;
      }
      const selectable = findSelectableElement(e.target, host);
      if (selectable) {
        const key = elementLayoutKeyFromElement(selectable);
        onSelectElementKey?.(key);
        if (key?.startsWith('slot:')) {
          onSelectPhotoSlot?.(key.slice(5));
        }
        return;
      }
      onSelectElementKey?.(null);
    };

    const onHostContextMenu = (e) => {
      if (e.target.matches?.('img[data-photo-slot]') && bindings[e.target.getAttribute('data-photo-slot')]) {
        e.preventDefault();
      }
    };

    const onHostMouseDown = (e) => {
      if (e.button !== 2 || photoSlotTool !== 'crop') return;
      const img = e.target.closest?.('img[data-photo-slot]');
      if (!img) return;
      const slot = img.getAttribute('data-photo-slot');
      const assetId = slot ? bindings[slot] : null;
      if (!slot || !assetId) return;
      e.preventDefault();
      const start = parseObjectPosition(
        img.style.objectPosition || slotPositions[slot] || '50% 50%',
      );
      const startX = e.clientX;
      const startY = e.clientY;
      panRef.current = { slot, img, start, startX, startY };

      const zoom = appliedPhotoZoom(img);
      const onMove = (ev) => {
        if (!panRef.current) return;
        const rect = img.getBoundingClientRect();
        const dx = ((ev.clientX - startX) / Math.max(rect.width, 1)) * 100;
        const dy = ((ev.clientY - startY) / Math.max(rect.height, 1)) * 100;
        const op = formatObjectPosition(start.x - dx, start.y - dy);
        img.style.objectPosition = op;
        if (zoom !== 1) applyPhotoZoom(img, zoom, op);
      };

      const onUp = () => {
        if (panRef.current && onPhotoObjectPositionChange) {
          onPhotoObjectPositionChange(
            panRef.current.slot,
            img.style.objectPosition || formatObjectPosition(start.x, start.y),
          );
        }
        panRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    return (
      <div
        ref={ref}
        className={`mc-artboard mc-artboard-html${presentation ? ' mc-artboard-presentation' : ''}`}
        style={{
          width: w * scale,
          height: h * scale,
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <style data-mc-export-quarantine="document">{artboardCss}</style>
        <div
          className="mc-html-scaler"
          style={{
            width: w * scale,
            height: h * scale,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div
            className="mc-html-host"
            style={{
              width: w,
              height: h,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              position: 'relative',
            }}
            onClick={presentation ? undefined : onHostClick}
            onContextMenu={presentation ? undefined : onHostContextMenu}
            onMouseDown={presentation ? undefined : onHostMouseDown}
            onKeyDown={() => undefined}
            role="presentation"
          >
            <div
              ref={hostRef}
              className="mc-html-stage-inner"
              style={{ width: w, height: h }}
            />
            {!presentation ? (
              <MediaCreatorHtmlSelectionOverlay
                hostRef={hostRef}
                scale={scale}
                selectedKey={selectedElementKey}
                onSelectKey={onSelectElementKey}
                elementLayout={elementLayout}
                onElementLayoutChange={onElementLayoutChange}
                onRequestDelete={onDeleteElement}
              />
            ) : null}
          </div>
        </div>
        <style data-mc-export-quarantine="host">{`
          .mc-artboard-html .mc-html-host [data-editable] {
            outline: none;
            cursor: text;
          }
          .mc-artboard-html .mc-html-host [data-editable]:focus {
            box-shadow: 0 0 0 2px rgba(249, 190, 11, 0.45);
            border-radius: 2px;
          }
          .mc-artboard-html .mc-html-host img[data-photo-slot],
          .mc-artboard-html .mc-html-host img[data-logo-slot],
          .mc-artboard-html .mc-html-host img.mc-brand {
            cursor: pointer;
          }
          .mc-artboard-html .mc-html-host img[data-photo-slot="${selectedPhotoSlot || ''}"] {
            box-shadow: inset 0 0 0 3px #f9be0b;
          }
          .mc-artboard-html .mc-html-host .mc-slot-caption {
            display: ${showSlotCaptions ? 'block' : 'none'};
          }
          .mc-artboard-html .mc-html-host .mc-copy,
          .mc-artboard-html .mc-html-host .mc-gallery,
          .mc-artboard-html .mc-html-host .mc-invite {
            cursor: default;
          }
        `}</style>
      </div>
    );
  },
);

MediaCreatorHtmlArtboard.displayName = 'MediaCreatorHtmlArtboard';

export default MediaCreatorHtmlArtboard;
